import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { apiFetch } from "../utils/api";
import { PrimaryButton, GhostButton } from "../../design-system";
import { FormField, Input, TextArea } from "../../design-system/components/inputs";
import { typography, borders, surfaces, textColors } from "../../design-system/tokens";

const UnsubscribeMarketing = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return (params.get("token") || "").trim();
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(null);
  const [error, setError] = useState("");

  const [confirm, setConfirm] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      setValid(false);
      setEmail("");
      setIsSubscribed(null);
      try {
        if (!token) {
          setError("Missing unsubscribe token.");
          return;
        }
        const resp = await apiFetch(`/api/unsubscribe/marketing/validate/?token=${encodeURIComponent(token)}`, {
          credentials: "include",
        });
        const data = await resp.json().catch(() => ({}));
        if (!mounted) return;
        if (!resp.ok || !data?.ok) {
          setError("This unsubscribe link is invalid or expired.");
          return;
        }
        setValid(true);
        setEmail(data.email || "");
        setIsSubscribed(!!data.is_marketing_subscribed);
      } catch (_e) {
        if (!mounted) return;
        setError("We couldn't validate this link right now. Please try again later.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const resp = await apiFetch("/api/unsubscribe/marketing/confirm/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, confirm: true, feedback }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.ok) {
        setError("Unable to unsubscribe with this link. It may have expired.");
        return;
      }
      setSuccess("You're unsubscribed from marketing emails. Thank you!");
      setIsSubscribed(false);
    } catch (_e) {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Marketing preferences" subtitle="Unsubscribe from marketing emails">
      <h2 className={`${typography.h2} ${textColors.primary} mb-6 text-center`}>Unsubscribe</h2>

      {loading ? (
        <div className={`mb-4 p-3 ${surfaces.secondary} border ${borders.default} rounded-md text-center ${textColors.secondary}`}>
          Validating link…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md text-center">{error}</div>
      ) : null}

      {success ? (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-md text-center">{success}</div>
      ) : null}

      {valid && !loading ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!confirm) return;
            submit();
          }}
          className="space-y-6"
        >
          <FormField label="Email" htmlFor="email">
            <Input id="email" type="email" value={email} disabled />
          </FormField>

          {isSubscribed === false ? (
            <div className={`${typography.body} ${textColors.secondary} text-center`}>
              This email is already unsubscribed from marketing emails.
            </div>
          ) : (
            <>
              <div className={`p-3 ${surfaces.secondary} border ${borders.default} rounded-md`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                    className={`mt-1 h-4 w-4 rounded border ${borders.strong} accent-blue-600 dark:accent-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400`}
                  />
                  <span className={`${typography.bodySmall} ${textColors.primary}`}>
                    Yes, unsubscribe me from marketing emails.
                  </span>
                </label>
              </div>

              <FormField label="Optional: tell us why (helps us improve)" htmlFor="feedback" optional>
                <TextArea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={5}
                  placeholder="What made you unsubscribe?"
                />
              </FormField>

              <PrimaryButton type="submit" variant="gradient" fullWidth={true} disabled={!confirm || submitting}>
                {submitting ? "Unsubscribing…" : "Confirm unsubscribe"}
              </PrimaryButton>
            </>
          )}

          <div className="text-center">
            <GhostButton
              type="button"
              variant="primary"
              onClick={() => navigate("/")}
              className={`${typography.bodySmall}`}
            >
              Back to home
            </GhostButton>
          </div>
        </form>
      ) : null}
    </AuthLayout>
  );
};

export default UnsubscribeMarketing;

